"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = buildTROGTimeline;
require("regenerator-runtime/runtime");
// setup
const helpers_1 = require("../shared/helpers");
const taskSetup_1 = require("../taskSetup");
// trials
const trials_1 = require("../shared/trials");
const config_1 = require("./helpers/config");
const prepareCat_1 = require("../shared/helpers/prepareCat");
const taskStore_1 = require("../../taskStore");
const preloadSharedAudio_1 = require("../shared/helpers/preloadSharedAudio");
let trogValidityEvaluator = null;
async function buildTROGTimeline(config, mediaAssets) {
    const preloadTrials = (0, helpers_1.createPreloadTrials)(mediaAssets).default;
    (0, taskStore_1.taskStore)('currentCatBlock', 0);
    (0, helpers_1.initTrialSaving)(config);
    const initialTimeline = (0, helpers_1.initTimeline)(config, trials_1.enterFullscreen);
    const timeline = [];
    const corpus = (0, taskStore_1.taskStore)().corpora.stimulus;
    const translations = (0, taskStore_1.taskStore)().translations;
    const validationErrorMap = {};
    const { runCat } = (0, taskStore_1.taskStore)();
    const { semThreshold } = (0, taskStore_1.taskStore)();
    const layoutConfigMap = {};
    for (const c of corpus) {
        const { itemConfig, errorMessages } = (0, config_1.getLayoutConfig)(c, translations, mediaAssets);
        layoutConfigMap[c.itemId] = itemConfig;
        if (errorMessages.length) {
            validationErrorMap[c.itemId] = errorMessages.join('; ');
        }
    }
    if (Object.keys(validationErrorMap).length) {
        console.error('The following errors were found');
        console.table(validationErrorMap);
        throw new Error('Something went wrong. Please look in the console for error details');
    }
    // organize media assets into batches for preloading
    const batchSize = 25;
    const batchedCorpus = (0, helpers_1.batchTrials)(corpus, batchSize);
    const batchedMediaAssets = (0, helpers_1.batchMediaAssets)(mediaAssets, batchedCorpus, ['answer', 'distractors']);
    // counter for next batch to preload (skipping the initial preload)
    let currPreloadBatch = 0;
    if ((0, taskStore_1.taskStore)().isRoarApp) {
        timeline.push(initialTimeline, preloadTrials);
    }
    else {
        const initialPreload = runCat ? preloadTrials : (0, preloadSharedAudio_1.preloadSharedAudio)();
        timeline.push(initialPreload, initialTimeline);
    }
    // does not matter if trial has properties that don't belong to that type
    const trialConfig = {
        trialType: 'audio',
        responseAllowed: true,
        promptAboveButtons: true,
        task: config.task,
        layoutConfig: {
            showPrompt: false,
        },
        layoutConfigMap,
        terminateCat: false,
    };
    const stimulusBlock = {
        timeline: [(0, trials_1.afcStimulusTemplate)(trialConfig, undefined)],
        // true = execute normally, false = skip
        conditional_function: () => {
            if ((0, taskStore_1.taskStore)().skipCurrentTrial) {
                (0, taskStore_1.taskStore)('skipCurrentTrial', false);
                return false;
            }
            if (runCat && taskSetup_1.cat._seMeasurement < semThreshold) {
                return false;
            }
            return true;
        },
    };
    function preloadBatch() {
        timeline.push((0, helpers_1.createPreloadTrials)(batchedMediaAssets[currPreloadBatch]).default);
        currPreloadBatch++;
    }
    if (runCat) {
        // seperate out corpus to get cat/non-cat blocks
        const corpora = (0, prepareCat_1.prepareCorpus)(corpus);
        // instruction block (non-cat)
        corpora.ipLight.forEach((trial) => {
            timeline.push({ ...trials_1.fixationOnly, stimulus: '' });
            timeline.push((0, trials_1.afcStimulusTemplate)(trialConfig, trial));
        });
        // push in practice transition
        if (corpora.ipLight.filter((trial) => trial.assessmentStage === 'practice_response').length > 0) {
            timeline.push((0, trials_1.practiceTransition)());
        }
        // push in starting block
        corpora.start.forEach((trial) => {
            timeline.push({ ...trials_1.fixationOnly, stimulus: '' });
            timeline.push((0, trials_1.afcStimulusTemplate)(trialConfig, trial));
        });
        // cat block
        const numOfCatTrials = corpora.cat.length;
        (0, taskStore_1.taskStore)('totalTestTrials', numOfCatTrials);
        for (let i = 0; i < numOfCatTrials; i++) {
            timeline.push({ ...trials_1.setupStimulus, stimulus: '' });
            timeline.push(stimulusBlock);
        }
        // select up to 5 random items from unnormed portion of corpus
        const unnormedTrials = (0, prepareCat_1.selectNItems)(corpora.unnormed, 5);
        // random set of unvalidated items at end
        const unnormedBlock = {
            timeline: unnormedTrials.map((trial) => (0, trials_1.afcStimulusTemplate)(trialConfig, trial)),
        };
        timeline.push(unnormedBlock);
    }
    else if ((0, helpers_1.shouldUseClowder)()) {
        await (0, taskSetup_1.initializeClowder)(config);
        trogValidityEvaluator = (0, helpers_1.createValidityEvaluator)(config);
        const instructionTrials = corpus.filter((trial) => { var _a, _b; return ((_a = trial.trialType) === null || _a === void 0 ? void 0 : _a.includes('instruction')) || ((_b = trial.assessmentStage) === null || _b === void 0 ? void 0 : _b.includes('instruction')); });
        for (const trial of instructionTrials) {
            timeline.push({ ...trials_1.fixationOnly, stimulus: '' });
            timeline.push((0, trials_1.afcStimulusTemplate)(trialConfig, trial));
        }
        (0, taskSetup_1.setNextStimulus)({ ignorePreviousItem: true });
        const practiceBlock = {
            timeline: [{ ...trials_1.setupStimulus, stimulus: '' }, (0, trials_1.afcStimulusTemplate)(trialConfig)],
            // Skip the block entirely if the Clowder cat yielded no item. Without this guard,
            // afcStimulus renders a null stimulus (stim.itemId on null) and nothing appears.
            conditional_function: () => (0, taskStore_1.taskStore)().nextStimulus != undefined,
            loop_function: () => {
                return (0, taskStore_1.taskStore)().nextStimulus != undefined;
            },
            on_timeline_finish: () => {
                (0, taskSetup_1.setNextStimulus)();
            },
        };
        timeline.push(practiceBlock);
        // Overwrite conditional with Clowder-specific logic
        timeline.push({ ...trials_1.setupStimulus, stimulus: '' });
        timeline.push({
            ...(0, trials_1.practiceTransition)(),
            conditional_function: () => {
                // Prevents last practice question from being added to seenItems twice
                (0, taskSetup_1.setNextStimulus)({ ignorePreviousItem: true });
                return (0, taskStore_1.taskStore)().currentCatBlock > 0;
            },
        });
        const testBlock = {
            timeline: [
                { ...trials_1.setupStimulus, stimulus: '' },
                (0, trials_1.afcStimulusTemplate)({ ...trialConfig, validityEvaluator: trogValidityEvaluator }),
            ],
            // Same guard as the practice block: don't render when the cat selection is empty.
            conditional_function: () => (0, taskStore_1.taskStore)().nextStimulus != undefined,
            loop_function: () => {
                (0, taskSetup_1.setNextStimulus)();
                return (0, taskStore_1.taskStore)().nextStimulus != undefined;
            },
            on_timeline_finish: () => {
                const { task, scoringVersion, currentCatBlock } = (0, taskStore_1.taskStore)();
                const { catOrderMap } = helpers_1.CLOWDER_SELECTION_CONFIG[task][scoringVersion];
                const currentCat = catOrderMap[currentCatBlock];
                const seenCount = taskSetup_1.seenCatItemsCount[currentCat] || 0;
                if (currentCat === 'composite' && seenCount > 0 && seenCount % 5 === 0) {
                    (0, taskSetup_1.moveToNextBlock)(2);
                    return;
                }
                if (currentCat === 'new') {
                    (0, taskSetup_1.moveToNextBlock)(1);
                }
            },
        };
        timeline.push(testBlock);
    }
    else {
        const numOfTrials = (0, taskStore_1.taskStore)().totalTrials;
        (0, taskStore_1.taskStore)('totalTestTrials', (0, helpers_1.getRealTrials)(corpus));
        for (let i = 0; i < numOfTrials; i++) {
            if (!(0, taskStore_1.taskStore)().isRoarApp && i % batchSize === 0) {
                preloadBatch();
            }
            timeline.push({ ...trials_1.setupStimulus, stimulus: '' });
            if ((0, taskStore_1.taskStore)().isRoarApp)
                timeline.push((0, trials_1.practiceTransition)()); // Levante does not have practice trials
            timeline.push(stimulusBlock);
        }
    }
    (0, taskSetup_1.initializeCat)();
    // final screens
    timeline.push((0, trials_1.taskFinished)());
    timeline.push(trials_1.exitFullscreen);
    return { jsPsych: taskSetup_1.jsPsych, timeline };
}
